import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { createRisk } from "../riskService";

const toOptionalNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? value : num;
};

const numberOptional = z.preprocess(toOptionalNumber, z.number().nullable());

const scoreOptional = numberOptional.refine(
  (val) => val === null || (Number.isInteger(val) && val >= 1 && val <= 5),
  { message: "Value must be an integer between 1 and 5." }
);

const schema = z.object({
  category: z.string().optional().nullable(),
  subcategory: z.string().optional().nullable(),
  name: z.string().min(1, "Risk Name is required."),
  description: z.string().optional().nullable(),
  impact_implications: z.string().optional().nullable(),
  causes: z.string().optional().nullable(),
  risk_type: z.string().optional().nullable(),
  likelihood: scoreOptional,
  impact: scoreOptional,
  owner_id: z.string().optional().nullable(),
  editor_id: z.string().optional().nullable(),
  matrix_id: numberOptional,
});

const initialTrackedState = {
  name: "",
  owner_id: "",
  editor_id: "",
  likelihood: null,
  impact: null,
  impact_theme_id: null,
  description: "",
  causes: "",
  impact_implications: "",
};

export default function useAddRiskForm({ onClose, onSaved }) {
  const [formState, setFormState] = useState(initialTrackedState);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [serverFieldErrors, setServerFieldErrors] = useState({});

  const defaultValues = useMemo(
    () => ({
      category: "",
      subcategory: "",
      name: "",
      description: "",
      impact_implications: "",
      causes: "",
      risk_type: "",
      likelihood: "",
      impact: "",
      owner_id: "",
      editor_id: "",
      matrix_id: null,
    }),
    []
  );

  const setters = useMemo(
    () => ({
      setName: (value) => setFormState((prev) => ({ ...prev, name: value || "" })),
      setOwnerId: (value) => setFormState((prev) => ({ ...prev, owner_id: value || "" })),
      setEditorId: (value) => setFormState((prev) => ({ ...prev, editor_id: value || "" })),
      setLikelihood: (value) => setFormState((prev) => ({ ...prev, likelihood: value ?? null })),
      setImpact: (value) => setFormState((prev) => ({ ...prev, impact: value ?? null })),
      setImpactThemeId: (value) => setFormState((prev) => ({ ...prev, impact_theme_id: value ?? null })),
      setDescription: (value) => setFormState((prev) => ({ ...prev, description: value || "" })),
      setCauses: (value) => setFormState((prev) => ({ ...prev, causes: value || "" })),
      setImpactImplications: (value) =>
        setFormState((prev) => ({ ...prev, impact_implications: value || "" })),
    }),
    []
  );

  const submitRisk = useCallback(
    async ({ values, impactThemeId, defaultMatrixId }) => {
      setFormError("");
      setServerFieldErrors({});
      setLoading(true);
      try {
        await createRisk({
          name: values.name.trim(),
          category: values.category?.trim() || null,
          subcategory: values.subcategory?.trim() || null,
          description: values.description || null,
          impact_implications: values.impact_implications || null,
          causes: values.causes || null,
          risk_type: values.risk_type || null,
          likelihood: values.likelihood ?? null,
          impact: values.impact ?? null,
          status: "Draft",
          owner_id: values.owner_id || null,
          editor_id: values.editor_id || null,
          matrix_id: values.matrix_id ?? defaultMatrixId ?? null,
          impact_theme_id: impactThemeId ?? null,
        });

        onSaved?.();
        onClose?.();
      } catch (err) {
        setServerFieldErrors(err?.fieldErrors || err?.response?.data?.field_errors || {});
        setFormError(err?.response?.data?.error || err?.message || "Failed to create risk");
      } finally {
        setLoading(false);
      }
    },
    [onClose, onSaved]
  );

  return {
    schema,
    defaultValues,
    formState,
    setters,
    loading,
    formError,
    serverFieldErrors,
    setFormError,
    setServerFieldErrors,
    submitRisk,
  };
}
